import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowUpRight, ArrowRight } from 'lucide-react';
import { COMPANY, NAV_LINKS, SERVICES } from '@/lib/data';
import Logo from './Logo';
import { subscribeNewsletter } from '@/lib/api';
import { toast } from 'sonner';

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const year = new Date().getFullYear();

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      const r = await subscribeNewsletter(email.trim());
      toast.success(r.message || 'Subscribed.');
      setEmail('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Subscription failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="section-dark relative overflow-hidden" data-testid="site-footer">
      <div className="pointer-events-none absolute inset-0 opacity-[0.06] bg-grain" />
      <div className="rule-horizontal absolute left-0 right-0 top-0" />

      {/* Big brand line */}
      <div className="container-x pt-24 pb-16">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <span className="eyebrow text-gold">Get in touch</span>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight mt-6">
              Let’s put structured leasing<br /> to work on your next project.
            </h2>
            <p className="mt-6 text-ivory-200/70 max-w-lg text-pretty">
              Talk to our team about scaffold, formwork, acrow-prop, column and containerised site-office packages tailored to your programme.
            </p>
            <Link to="/contact" className="btn-gold mt-8" data-testid="footer-cta-contact">
              Start an Enquiry <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-10">
            <div>
              <h4 className="text-[11px] uppercase tracking-[0.28em] text-gold">Head Office</h4>
              <div className="mt-5 space-y-4 text-ivory-100/80 text-sm">
                <div className="flex gap-3"><MapPin className="w-4 h-4 mt-1 shrink-0 text-gold" /> <span>{COMPANY.headOffice}</span></div>
                <div className="flex gap-3"><Mail className="w-4 h-4 mt-1 shrink-0 text-gold" /> <a href={`mailto:${COMPANY.email}`} className="hover:text-gold transition-colors">{COMPANY.email}</a></div>
                <div className="flex gap-3"><Phone className="w-4 h-4 mt-1 shrink-0 text-gold" /> <a href={`tel:${COMPANY.phone.replace(/\s/g,'')}`} className="hover:text-gold transition-colors">{COMPANY.phone}</a></div>
              </div>
            </div>
            <div>
              <h4 className="text-[11px] uppercase tracking-[0.28em] text-gold">Newsletter</h4>
              <p className="mt-5 text-sm text-ivory-100/70">Quarterly insights on structured leasing in Nigeria’s construction sector.</p>
              <form onSubmit={onSubscribe} className="mt-5 flex items-end gap-3 border-b border-ivory-100/20 focus-within:border-gold transition-colors" data-testid="footer-newsletter-form">
                <input
                  type="email"
                  required
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="footer-newsletter-email"
                  className="flex-1 bg-transparent py-3 text-sm placeholder:text-ivory-100/40 focus:outline-none text-ivory-50"
                />
                <button type="submit" disabled={loading} data-testid="footer-newsletter-submit" className="pb-3 text-gold hover:text-gold-400 disabled:opacity-50 transition-colors">
                  <ArrowUpRight className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="rule-horizontal" />

      {/* Links */}
      <div className="container-x py-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Logo variant="dark" />
          <p className="mt-6 text-sm text-ivory-100/60 max-w-md">
            {COMPANY.legalName} — a Nigerian asset-leasing and logistics support company delivering flexible, efficient, and scalable leasing solutions across construction, real estate, infrastructure, and allied sectors.
          </p>
          <div className="mt-6 text-[11px] uppercase tracking-[0.28em] text-gold/80">{COMPANY.rc}</div>
        </div>

        <div className="lg:col-span-3">
          <h5 className="text-[11px] uppercase tracking-[0.28em] text-gold">Explore</h5>
          <ul className="mt-5 space-y-3 text-sm">
            {NAV_LINKS.map(l => (
              <li key={l.to}><Link to={l.to} className="text-ivory-100/70 hover:text-gold transition-colors" data-testid={`footer-link-${l.label.toLowerCase()}`}>{l.label}</Link></li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h5 className="text-[11px] uppercase tracking-[0.28em] text-gold">Services</h5>
          <ul className="mt-5 space-y-3 text-sm">
            {SERVICES.map(s => (
              <li key={s.slug}><Link to={`/services/${s.slug}`} className="text-ivory-100/70 hover:text-gold transition-colors">{s.title}</Link></li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h5 className="text-[11px] uppercase tracking-[0.28em] text-gold">Legal</h5>
          <ul className="mt-5 space-y-3 text-sm">
            <li><Link to="/privacy" className="text-ivory-100/70 hover:text-gold transition-colors">Privacy Policy</Link></li>
            <li><Link to="/terms" className="text-ivory-100/70 hover:text-gold transition-colors">Terms of Use</Link></li>
            <li><Link to="/admin" className="text-ivory-100/60 hover:text-gold transition-colors">Admin</Link></li>
          </ul>
        </div>
      </div>

      <div className="rule-horizontal" />

      <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[11px] uppercase tracking-[0.24em] text-ivory-100/50">
        <div>© {year} {COMPANY.legalName}. All rights reserved.</div>
        <div>Site built for institutional-grade governance and transparency.</div>
      </div>
    </footer>
  );
};

export default Footer;
